import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomEditor from '../../custom/CustomEditor';
import apiClient from '../../../api/apiClient';
import * as bootstrap from 'bootstrap';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { generateDescription, generateText } from "../../custom/geminiService";

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const isEditorEmpty = (htmlString) => {
    if (!htmlString) return true;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent.trim().length === 0;
};

const InputField = ({ name, placeholder, value, onChange, error, type = "text", ...props }) => (
    <div
        className={`floating-field position-relative ${value ? 'has-value' : ''}`}
        data-bs-toggle={error ? "tooltip" : undefined}
        data-bs-placement="top"
        title={error || ""}
    >
        <input
            type={type}
            name={name}
            className={`form-control ${error ? 'is-invalid' : ''}`}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            {...props}
        />
        <label className="floating-label">{placeholder}</label>
    </div>
);

const libraries = ['places'];

function FormHotel() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAP_KEY,
        libraries,
    });

    const autocompleteRef = useRef(null);
    const collapseTwoRef = useRef(null);
    const collapseThreeRef = useRef(null);

    // UPDATED: Added 'category' to the initial state
    const [formData, setFormData] = useState({
        hotel_name: '', address: '', city: '', state: '',
        pincode: '', country: 'India', hotel_price: '', offer_price: '',
        category: '',
        description: '', hotel_overview: '', booking_policy: '',
        places_to_visit: '', amenities: [],
    });

    const [existingImages, setExistingImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [imagesToRemove, setImagesToRemove] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const [isGeneratingAIDescription, setIsGeneratingAIDescription] = useState(false);

    const HOTEL_CATEGORIES = [
        { value: "1 Star", label: "1 Star" },
        { value: "2 Star", label: "2 Star" },
        { value: "3 Star", label: "3 Star" },
        { value: "4 Star", label: "4 Star" },
        { value: "5 Star", label: "5 Star" },
        { value: "6 Star", label: "6 Star" },
        { value: "7 Star", label: "7 Star" },
    ];

    const AMENITIES_LIST = [
        { id: 'wifi', name: 'WiFi', icon: 'ri-wifi-line' }, { id: 'parking', name: 'Parking', icon: 'ri-car-line' },
        { id: 'pool', name: 'Swimming Pool', icon: 'ri-water-flash-line' }, { id: 'restaurant', name: 'Restaurant', icon: 'ri-restaurant-2-line' },
        { id: 'pet_friendly', name: 'Pet Friendly', icon: 'ri-baidu-line' }, { id: 'gym', name: 'Gym/Fitness Center', icon: 'ri-run-line' },
        { id: 'ac', name: 'Air Conditioning', icon: 'ri-windy-line' }, { id: 'room_service', name: 'Room Service', icon: 'ri-customer-service-2-line' },
        { id: 'telephone', name: 'Telephone', icon: 'ri-phone-line' }, { id: 'geyser', name: 'Geyser / Hot Water', icon: 'ri-shower-line' },
        { id: 'spa', name: 'Spa', icon: 'ri-plant-line' }, { id: 'bar', name: 'Bar/Lounge', icon: 'ri-goblet-line' },
    ];

    const imageGroups = useMemo(() => {
        if (!existingImages || existingImages.length === 0) return [];
        const groups = existingImages.reduce((acc, image) => {
            const urlParts = image.image_url.split('/');
            const fileName = urlParts[urlParts.length - 1];
            const baseName = fileName.substring(0, fileName.lastIndexOf('_'));
            if (!acc[baseName]) acc[baseName] = [];
            acc[baseName].push(image);
            return acc;
        }, {});
        return Object.values(groups);
    }, [existingImages]);

    useEffect(() => {
        const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
        const tooltipList = tooltipTriggerList.map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl));
        return () => tooltipList.forEach(tooltip => tooltip && tooltip.dispose());
    }, [errors]);

    useEffect(() => {
        if (isEditMode) {
            const fetchHotelData = async () => {
                try {
                    const response = await apiClient.get(`/hotels/${id}`);
                    const hotel = response.data;
                    const amenities = typeof hotel.amenities === 'string' ? JSON.parse(hotel.amenities) : hotel.amenities || [];
                    setExistingImages(hotel.Images || []);
                    const { Images, ...restOfHotelData } = hotel;
                    setFormData(prevData => ({ ...prevData, ...restOfHotelData, amenities }));
                } catch (error) {
                    toast.error("Failed to load hotel data. Please try again.");
                    navigate('/admin/hotels');
                } finally {
                    setIsLoading(false);
                }
            };
            fetchHotelData();
        }
    }, [id, isEditMode, navigate]);

    useEffect(() => {
        if (loadError) {
            toast.error("Google Maps script could not be loaded. Address autocomplete will not work.");
            console.error("Google Maps load error:", loadError);
        }
    }, [loadError]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleEditorChange = (fieldName, value) => {
        setFormData((prev) => ({ ...prev, [fieldName]: value }));
        if (errors[fieldName]) setErrors(prev => ({ ...prev, [fieldName]: null }));
    };

    const handleAmenityChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => {
            const currentAmenities = prev.amenities;
            if (checked) return { ...prev, amenities: [...currentAmenities, name] };
            return { ...prev, amenities: currentAmenities.filter((amenity) => amenity !== name) };
        });
        if (errors.amenities) setErrors(prev => ({ ...prev, amenities: null }));
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        if ((imageGroups.length + newImages.length + files.length) > 6) {
            toast.warn("You can have a maximum of 6 images in total.");
            return;
        }
        const newImageFiles = files.map(file => ({ file, url: URL.createObjectURL(file) }));
        setNewImages(prev => [...prev, ...newImageFiles]);
        if (errors.images) setErrors(prev => ({ ...prev, images: null }));
    };

    const handleRemoveNewImage = (index) => {
        const updatedImages = [...newImages];
        URL.revokeObjectURL(updatedImages[index].url);
        updatedImages.splice(index, 1);
        setNewImages(updatedImages);
    };

    const handleRemoveExistingImage = (imageGroup) => {
        const representativeUrl = imageGroup[0]?.image_url;
        if (!representativeUrl) return;
        setImagesToRemove(prev => [...prev, representativeUrl]);
        const baseName = representativeUrl.substring(representativeUrl.lastIndexOf('/') + 1, representativeUrl.lastIndexOf('_'));
        setExistingImages(prev => prev.filter(img => !img.image_url.includes(baseName)));
    };

    const handleAutocompleteLoad = (autocomplete) => {
        autocompleteRef.current = autocomplete;
    };

    const handlePlaceSelect = () => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (!place || !place.address_components) {
                toast.warn("Could not retrieve address details. Please select from the dropdown or enter manually.");
                return;
            }

            const componentMap = {
                locality: 'city',
                administrative_area_level_1: 'state',
                country: 'country',
                postal_code: 'pincode',
            };
            const newAddress = { city: '', state: '', country: '', pincode: '' };

            place.address_components.forEach(component => {
                const type = component.types[0];
                if (componentMap[type]) {
                    newAddress[componentMap[type]] = component.long_name;
                }
            });

            setFormData(prev => ({
                ...prev,
                hotel_name: place.name || prev.hotel_name,
                address: place.formatted_address || '',
                city: newAddress.city || prev.city,
                state: newAddress.state || prev.state,
                country: newAddress.country || prev.country,
                pincode: newAddress.pincode || prev.pincode,
            }));

            setErrors(prev => ({
                ...prev, hotel_name: null, address: null, city: null,
                state: null, country: null, pincode: null,
            }));
        }
    };

    const handleGenerateOverview = async () => {
        const { hotel_name, city, hotel_price } = formData;

        if (!hotel_name || !city || !hotel_price) {
            toast.warn("Please provide Hotel Name, City, and Package Price before generating.");
            return;
        }

        setIsGeneratingAI(true);
        try {
            const aiContent = await generateText(hotel_name, city, hotel_price);
            handleEditorChange('hotel_overview', aiContent);
            toast.success("AI overview generated successfully!");
        } catch (error) {
            toast.error(error.message || "Failed to generate AI overview. Please try again.");
            console.error("Error calling generateOverview:", error);
        } finally {
            setIsGeneratingAI(false);
        }
    };

    const handleGenerateDescription = async () => {
        const { hotel_name, city, hotel_price } = formData;

        if (!hotel_name || !city || !hotel_price) {
            toast.warn("Please provide Hotel Name, City, and Package Price before generating.");
            return;
        }

        setIsGeneratingAIDescription(true);
        try {
            const aiContent = await generateDescription(hotel_name, city);
            handleEditorChange('description', aiContent);
            toast.success("AI description generated successfully!");
        } catch (error) {
            toast.error(error.message || "Failed to generate AI description. Please try again.");
            console.error("Error calling generateDescription:", error);
        } finally {
            setIsGeneratingAIDescription(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!String(formData.hotel_name || '').trim()) newErrors.hotel_name = 'Hotel name is required.';
        if (!String(formData.address || '').trim()) newErrors.address = 'Address is required.';
        if (!String(formData.city || '').trim()) newErrors.city = 'City is required.';
        if (!String(formData.state || '').trim()) newErrors.state = 'State is required.';
        if (!String(formData.country || '').trim()) newErrors.country = 'Country is required.';
        const pincodeStr = String(formData.pincode || '').trim();
        if (!pincodeStr) newErrors.pincode = 'Pincode is required.';
        else if (!/^\d{6}$/.test(pincodeStr)) newErrors.pincode = 'Pincode must be 6 digits.';
        if (!formData.category) newErrors.category = 'Hotel category is required.';

        if (!formData.hotel_price) newErrors.hotel_price = 'Price is required.';
        else if (isNaN(formData.hotel_price) || +formData.hotel_price <= 0) newErrors.hotel_price = 'Price must be a positive number.';
        if (!formData.offer_price) newErrors.offer_price = 'Offer price is required.';
        else if (isNaN(formData.offer_price)) newErrors.offer_price = 'Offer price must be a number.';
        else if (+formData.hotel_price && +formData.offer_price > +formData.hotel_price) newErrors.offer_price = 'Offer price cannot be greater than the regular price.';

        if (!String(formData.description || '').trim()) newErrors.description = 'Short description is required.';
        if (isEditorEmpty(formData.hotel_overview)) newErrors.hotel_overview = 'Hotel overview is required.';
        if (imageGroups.length === 0 && newImages.length === 0) newErrors.images = 'Please upload at least one image.';
        if (formData.amenities.length === 0) newErrors.amenities = 'Please select at least one amenity.';
        if (isEditorEmpty(formData.booking_policy)) newErrors.booking_policy = 'Booking policy is required.';
        if (!String(formData.places_to_visit || '').trim()) newErrors.places_to_visit = 'Places to visit is required.';
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            const errorKeys = Object.keys(formErrors);
            if (errorKeys.includes('images')) {
                const collapseElement = collapseTwoRef.current;
                if (collapseElement) bootstrap.Collapse.getOrCreateInstance(collapseElement).show();
            }
            const thirdAccordionErrors = ['amenities', 'booking_policy', 'places_to_visit'];
            if (errorKeys.some(key => thirdAccordionErrors.includes(key))) {
                const collapseElement = collapseThreeRef.current;
                if (collapseElement) bootstrap.Collapse.getOrCreateInstance(collapseElement).show();
            }
            setErrors(formErrors);
            toast.error("Please correct the errors before submitting.");
            return;
        }
        setErrors({});
        setIsSubmitting(true);
        const formDataUpload = new FormData();
        Object.entries(formData).forEach(([key, val]) => {
            if (key === 'amenities') formDataUpload.append(key, JSON.stringify(val));
            else if (!['Images', 'id', 'created_at', 'updated_at'].includes(key)) formDataUpload.append(key, val);
        });
        newImages.forEach((img) => formDataUpload.append('images', img.file));
        if (imagesToRemove.length > 0) formDataUpload.append('imagesToRemove', JSON.stringify(imagesToRemove));

        try {
            const config = { headers: { 'Content-Type': 'multipart/form-data' } };
            const successMessage = isEditMode ? 'Hotel updated successfully!' : 'Hotel created successfully!';
            if (isEditMode) {
                await apiClient.put(`/hotels/${id}`, formDataUpload, config);
            } else {
                await apiClient.post('/hotels', formDataUpload, config);
            }
            toast.success(successMessage);
            navigate('/admin/hotels', { state: { message: successMessage } });
        } catch (error) {
            toast.error(error.response?.data?.message || `An error occurred. Please try again.`);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading && isEditMode) return <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>;

    return (
        <div>
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0">{isEditMode ? 'Edit Hotel' : 'Create Hotel'}</h4>
                        <div className="page-title-right">
                            <ol className="breadcrumb m-0">
                                <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
                                <li className="breadcrumb-item"><Link to="/admin/hotels">Hotels</Link></li>
                                <li className="breadcrumb-item active">{isEditMode ? 'Edit' : 'Add'}</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <form onSubmit={handleSubmit} noValidate>
                <div className="card">
                    <div className="card-header"><h4 className="card-title mb-0">{isEditMode ? 'Edit Hotel Details' : 'Add New Hotel'}</h4></div>
                    <div className="card-body">
                        <div className="accordion" id="hotelAccordion">
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingOne"><button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne">Hotel Details</button></h2>
                                <div id="collapseOne" className="accordion-collapse collapse show" data-bs-parent="#hotelAccordion">
                                    <div className="accordion-body">
                                        <div className="row g-3">
                                            <div className="col-12">
                                                {isLoaded ? (
                                                    <Autocomplete onLoad={handleAutocompleteLoad} onPlaceChanged={handlePlaceSelect} options={{ componentRestrictions: { country: "in" }, fields: ["name", "formatted_address", "address_components", "geometry"] }}>
                                                        <InputField name="address" placeholder="Search for a hotel, building, or address..." value={formData.address} onChange={handleInputChange} error={errors.address} />
                                                    </Autocomplete>
                                                ) : (
                                                    <InputField name="address" placeholder="Loading maps..." value={formData.address} readOnly />
                                                )}
                                            </div>
                                            <div className="col-12"><InputField name="hotel_name" placeholder="Hotel Name (auto-filled from search)" value={formData.hotel_name} onChange={handleInputChange} error={errors.hotel_name} readOnly={isEditMode} /></div>
                                            <div className="col-md-4"><InputField name="city" placeholder="City" value={formData.city} onChange={handleInputChange} error={errors.city} /></div>
                                            <div className="col-md-4"><InputField name="state" placeholder="State" value={formData.state} onChange={handleInputChange} error={errors.state} /></div>
                                            <div className="col-md-4"><InputField name="pincode" placeholder="Pincode"
                                                value={formData.pincode} onChange={handleInputChange} error={errors.pincode} maxLength="6" /></div>
                                            <div className="col-md-3"><InputField name="country" placeholder="Country" value={formData.country} onChange={handleInputChange} error={errors.country} /></div>

                                            <div className="col-md-3">
                                                <div className={`floating-field position-relative ${formData.category ? 'has-value' : ''}`} data-bs-toggle={errors.category ? "tooltip" : undefined} data-bs-placement="top" title={errors.category || ""}>
                                                    <select
                                                        name="category"
                                                        className={`form-select ${errors.category ? 'is-invalid' : ''}`}
                                                        value={formData.category}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="">Select Hotel Category</option>
                                                        {HOTEL_CATEGORIES.map(cat => (
                                                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                                                        ))}
                                                    </select>
                                                    <label className="floating-label">Select Hotel Category</label>
                                                </div>
                                            </div>

                                            <div className="col-md-3"><InputField name="hotel_price" type="number" placeholder="Package Price (₹)" value={formData.hotel_price} onChange={handleInputChange} error={errors.hotel_price} /></div>
                                            <div className="col-md-3"><InputField name="offer_price" type="number" placeholder="Offer Price (₹)" value={formData.offer_price} onChange={handleInputChange} error={errors.offer_price} /></div>

                                            <div className="col-12">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <label className="form-label mb-0 fw-medium">Short Description</label>
                                                    <button type="button" className="btn btn-soft-primary btn-sm" onClick={handleGenerateDescription} disabled={isGeneratingAIDescription || isSubmitting}>
                                                        {isGeneratingAIDescription ? (<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Generating...</>) : (<><i className="ri-robot-2-line me-1"></i>Generate with AI</>)}
                                                    </button>
                                                </div>
                                                <div className={`floating-field position-relative ${formData.description ? 'has-value' : ''}`} data-bs-toggle={errors.description ? "tooltip" : undefined} data-bs-placement="top" title={errors.description || ""}>
                                                    <textarea name="description" className={`form-control ${errors.description ? 'is-invalid' : ''}`} placeholder="A brief, catchy summary of the hotel..." value={formData.description} onChange={handleInputChange} rows={3}></textarea>
                                                    <label className="floating-label">A brief, catchy summary of the hotel...</label>
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <label className="form-label mb-0 fw-medium">Hotel Overview</label>
                                                    <button type="button" className="btn btn-soft-primary btn-sm" onClick={handleGenerateOverview} disabled={isGeneratingAI || isSubmitting}>
                                                        {isGeneratingAI ? (<><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Generating...</>) : (<><i className="ri-robot-2-line me-1"></i>Generate with AI</>)}
                                                    </button>
                                                </div>
                                                <div className={errors.hotel_overview ? 'is-invalid-editor' : ''} data-bs-toggle={errors.hotel_overview ? "tooltip" : undefined} data-bs-placement="top" title={errors.hotel_overview || ""}>
                                                    <CustomEditor placeholder="Detailed hotel overview... or generate one with AI!" data={formData.hotel_overview} onChange={(val) => handleEditorChange('hotel_overview', val)} showHtmlMode={false} />
                                                </div>
                                                {errors.hotel_overview && <div className="d-block mt-1 text-danger" style={{ fontSize: '.875em' }}>{errors.hotel_overview}</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* --- Other accordion items remain unchanged --- */}
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingTwo"><button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo">Upload Hotel Images</button></h2>
                                <div id="collapseTwo" ref={collapseTwoRef} className="accordion-collapse collapse" data-bs-parent="#hotelAccordion">
                                    <div className="accordion-body">
                                        <div className="alert alert-info" role="alert"><strong>Note:</strong> Upload up to 6 images</div>
                                        <div className="d-flex flex-wrap gap-3">
                                            {imageGroups.map((group, index) => {
                                                const previewImage = group.find(img => img.image_size_name === 'thumbnail') || group[0];
                                                if (!previewImage) return null;
                                                return (
                                                    <div key={`existing-group-${index}`} className="position-relative" style={{ width: '120px', height: '120px' }}>
                                                        <img src={previewImage.image_url} alt={`Group ${index}`} className="img-thumbnail h-100 w-100" style={{ objectFit: 'cover' }} />
                                                        <button type="button" onClick={() => handleRemoveExistingImage(group)} className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-1" title="Remove">×</button>
                                                    </div>
                                                );
                                            })}
                                            {newImages.map((img, idx) => (
                                                <div key={`new-${idx}`} className="position-relative" style={{ width: '120px', height: '120px' }}>
                                                    <img src={img.url} alt={`Preview ${idx}`} className="img-thumbnail h-100 w-100" style={{ objectFit: 'cover' }} />
                                                    <button type="button" onClick={() => handleRemoveNewImage(idx)} className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-1" title="Remove">×</button>
                                                </div>
                                            ))}
                                            {(imageGroups.length + newImages.length) < 6 && (
                                                <label htmlFor="uploadImages" className="d-flex justify-content-center align-items-center" style={{ width: '120px', height: '120px', border: `2px dashed ${errors.images ? '#dc3545' : '#aaa'}`, borderRadius: '6px', cursor: 'pointer', fontSize: '32px', color: '#aaa' }}>
                                                    +
                                                    <input type="file" id="uploadImages" name="images" multiple hidden onChange={handleImageChange} accept="image/*" />
                                                </label>
                                            )}
                                        </div>
                                        {errors.images && <div className="d-block mt-2 text-danger" style={{ fontSize: '.875em' }}>{errors.images}</div>}
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item">
                                <h2 className="accordion-header" id="headingThree"><button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree">Additional Details & Policies</button></h2>
                                <div id="collapseThree" ref={collapseThreeRef} className="accordion-collapse collapse" data-bs-parent="#hotelAccordion">
                                    <div className="accordion-body">
                                        <div className="mb-4" data-bs-toggle={errors.amenities ? "tooltip" : undefined} data-bs-placement="top" title={errors.amenities || ""}>
                                            <h5 className="mb-3">Amenities</h5>
                                            <div className="row g-3">{AMENITIES_LIST.map(a => <div key={a.id} className="col-lg-3 col-md-4 col-sm-6"><div className="form-check custom-checkbox-card"><input className="form-check-input" type="checkbox" name={a.name} id={`a-${a.id}`} checked={formData.amenities.includes(a.name)} onChange={handleAmenityChange} /><label className="form-check-label" htmlFor={`a-${a.id}`}><i className={`${a.icon} me-2`}></i>{a.name}</label></div></div>)}</div>
                                            {errors.amenities && <div className="d-block mt-2 text-danger" style={{ fontSize: '.875em' }}>{errors.amenities}</div>}
                                        </div>
                                        <hr />
                                        <div className="mb-3" data-bs-toggle={errors.booking_policy ? "tooltip" : undefined} data-bs-placement="top" title={errors.booking_policy || ""}>
                                            <div className={errors.booking_policy ? 'is-invalid-editor' : ''}>
                                                <CustomEditor placeholder="Booking policy..." data={formData.booking_policy} onChange={(val) => handleEditorChange('booking_policy', val)} showHtmlMode={false} />
                                            </div>
                                            {errors.booking_policy && <div className="d-block mt-2 text-danger" style={{ fontSize: '.875em' }}>{errors.booking_policy}</div>}
                                        </div>
                                        <hr />
                                        <div className="col-12">
                                            <div className={`floating-field position-relative ${formData.places_to_visit ? 'has-value' : ''}`} data-bs-toggle={errors.places_to_visit ? "tooltip" : undefined} data-bs-placement="top" title={errors.places_to_visit || ""}>
                                                <textarea name="places_to_visit" className={`form-control ${errors.places_to_visit ? 'is-invalid' : ''}`} placeholder="Nearby places to visit..." value={formData.places_to_visit} onChange={handleInputChange} rows={3}></textarea>
                                                <label className="floating-label">Nearby places to visit...</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4 d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-light" onClick={() => navigate('/admin/hotels')} disabled={isSubmitting || isGeneratingAI || isGeneratingAIDescription}>Cancel</button>
                            <button type="submit" className="btn btn-success" disabled={isSubmitting || isGeneratingAI || isGeneratingAIDescription}>{isSubmitting ? 'Submitting...' : (isEditMode ? 'Update Hotel' : 'Submit Hotel')}</button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default FormHotel;